import { Component, Inject, OnInit } from "@angular/core";
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import { HttpClient, HttpEventType } from "@angular/common/http";
import { ToastrService } from "ngx-toastr";
import { FlowServiceService } from "src/app/Services/flow-service.service";
import { environment } from "src/environments/environment";
import { MediaUploadService } from "src/app/Services/media-upload.service";
import { ConfirmationDialogComponent } from "../user-dashboard/transfered-content-approvals/confirmation-dialog/confirmation-dialog.component";
import {
  LoadingModalComponent,
  LoadingModalData,
} from "../loading-modal/loading-modal.component";

@Component({
  selector: "app-flow-review-submit",
  templateUrl: "./flow-review-submit.component.html",
  styleUrls: ["./flow-review-submit.component.scss"],
})
export class FlowReviewSubmitComponent implements OnInit {
  flowData: any;
  userData: any;
  contentType: "flow" | "bundle" = "flow";
  /**
   * Indicates that this review dialog was opened immediately after
   * reporting issues (from the issue-reporting flow). In this case,
   * closing the dialog will also discard the reported issue data.
   */
  fromIssue: boolean = false;
  rating: number = 0;
  reviewText: string = "";
  selectedFile: File | null = null;
  mediaUrl: string = "";
  mediaThumbnailUrl: string = "";
  isVideo: boolean = false;
  isUploading: boolean = false;
  uploadProgress: number = 0;
  showPreview: boolean = false;
  loadingModalRef: MatDialogRef<LoadingModalComponent> = null;
  flowReviews: any[] = [];
  isLoadingReviews: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<FlowReviewSubmitComponent>,
    public dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private flowServiceService: FlowServiceService,
    private http: HttpClient,
    private toastr: ToastrService,
    private mediaUploadService: MediaUploadService
  ) {
    this.flowData = data.flowData;
    this.fromIssue = !!data?.fromIssue;
    this.contentType = data?.contentType === "bundle" ? "bundle" : "flow";
    this.userData = JSON.parse(localStorage.getItem("LoggedInUser"));
  }

  get contentTypeLabel(): string {
    return this.contentType === "bundle" ? "bundle" : "flow";
  }

  ngOnInit() {
    this.loadFlowReviews();
  }

  loadFlowReviews() {
    if (!this.flowData?.rowKey) return;
    
    this.isLoadingReviews = true;
    this.flowServiceService
      .getFlowReviews({ FlowRowKey: this.flowData.rowKey })
      .subscribe(
        (data: any) => {
          this.flowReviews = data.reviews || data || [];
          this.isLoadingReviews = false;
        },
        (error) => {
          console.error("Error loading reviews:", error);
          this.flowReviews = [];
          this.isLoadingReviews = false;
        }
      );
  }

  getAverageRating(): number {
    if (!this.flowReviews || this.flowReviews.length === 0) return 0;
    const reviewsWithRating = this.flowReviews.filter(r => r.rating && r.rating > 0);
    if (reviewsWithRating.length === 0) return 0;
    const sum = reviewsWithRating.reduce((acc, review) => acc + (review.rating || 0), 0);
    return Math.round((sum / reviewsWithRating.length) * 10) / 10;
  }

  formatDate(date: any): string {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  }

  async onFileSelected(event: any) {
    const file = event.target.files[0];
    if (!file) return;

    this.selectedFile = file;
    this.isUploading = true;
    this.uploadProgress = 0;

    const imageExtensions = [
      "jpeg", "jpg", "png", "gif", "jfif", "tiff", "tif", "pdf",
      "bmp", "webp", "svg", "heic", "heif", "raw", "arw", "cr2",
      "nef", "orf", "rw2", "dng", "psd", "ico", "exif", "pjpeg"
    ];
    const videoExtensions = [
      "mp4", "mov", "wmv", "flv", "avchd", "webm", "mkv", "vob",
      "ogv", "gifv", "m4p", "m4v", "3gp", "3g2", "avi", "mpg", "mpeg"
    ];

    const fileName = file.name.toLowerCase();
    const isImage = imageExtensions.some((ext) => fileName.includes(ext));
    const isVideo = videoExtensions.some((ext) => fileName.includes(ext));

    if (!isImage && !isVideo) {
      this.toastr.error("Please select an image or video file");
      this.selectedFile = null;
      this.isUploading = false;
      return;
    }

    this.isVideo = isVideo;

    const fileType = isImage ? "image" : "video";
    this.openLoadingModal(`Uploading ${fileType}...`, true);

    let fileToUpload = file;
    if (isVideo) {
      try {
        this.updateLoadingModal("Optimizing video for upload...", 0);
        fileToUpload = await this.mediaUploadService.prepareFileForUpload(file, (progress) => {
          this.updateLoadingModal("Optimizing video for upload...", progress);
        });
        this.selectedFile = fileToUpload;
      } catch (error: any) {
        this.isUploading = false;
        this.closeLoadingModal();
        this.selectedFile = null;
        this.toastr.error(error?.message || this.mediaUploadService.uploadTooLargeMessage());
        return;
      }
    }

    const formData = new FormData();
    formData.append("fileData", fileToUpload);

    const uploadUrl = isImage
      ? environment.apiBaseUrl + environment.uploadImageUrl
      : environment.apiBaseUrl + environment.uploadVideoUrl;

    this.http
      .post(uploadUrl, formData, {
        reportProgress: true,
        observe: "events",
      })
      .subscribe(
        (event: any) => {
          switch (event.type) {
            case HttpEventType.UploadProgress:
              if (event.total) {
                this.uploadProgress = Math.round(
                  (event.loaded / event.total) * 100
                );
                this.updateLoadingModal(undefined, this.uploadProgress);
              }
              break;

            case HttpEventType.Response:
              const mediaUploadStatus = event.body as any;
              this.isUploading = false;
              this.closeLoadingModal();

              if (mediaUploadStatus != null && mediaUploadStatus.status) {
                if (isImage) {
                  this.mediaUrl =
                    environment.imageStorageBaseUrl +
                    mediaUploadStatus.fileName;
                  this.mediaThumbnailUrl = this.mediaUrl;
                  this.showPreview = true;
                } else if (isVideo) {
                  this.mediaUrl =
                    environment.videoStorageBaseUrl +
                    mediaUploadStatus.fileName;
                  this.mediaThumbnailUrl = this.mediaUrl;
                  this.showPreview = true;
                }
                this.toastr.success("Media uploaded successfully");
              } else {
                this.toastr.error(mediaUploadStatus?.message || "Upload failed");
                this.selectedFile = null;
              }
              break;
          }
        },
        (error) => {
          this.isUploading = false;
          this.closeLoadingModal();
          this.toastr.error("Error uploading media");
          this.selectedFile = null;
        }
      );
  }

  // Loading modal helper methods
  openLoadingModal(message: string, showProgress: boolean = false) {
    if (this.loadingModalRef) {
      this.closeLoadingModal();
    }
    this.loadingModalRef = this.dialog.open(LoadingModalComponent, {
      width: "400px",
      disableClose: true,
      data: {
        message: message,
        progress: 0,
        showProgress: showProgress,
      } as LoadingModalData,
    });
  }

  updateLoadingModal(message?: string, progress?: number) {
    if (this.loadingModalRef && this.loadingModalRef.componentInstance) {
      if (message) {
        this.loadingModalRef.componentInstance.updateMessage(message);
      }
      if (progress !== undefined) {
        this.loadingModalRef.componentInstance.updateProgress(progress);
      }
    }
  }

  closeLoadingModal() {
    if (this.loadingModalRef) {
      this.loadingModalRef.close();
      this.loadingModalRef = null;
    }
  }

  removeMedia() {
    this.selectedFile = null;
    this.mediaUrl = "";
    this.mediaThumbnailUrl = "";
    this.showPreview = false;
    this.isVideo = false;
  }

  submit() {
    if (this.rating === 0) {
      this.toastr.warning("Please provide a rating");
      return;
    }

    const reviewData = {
      rating: this.rating,
      reviewText: this.reviewText || null,
      mediaUrl: this.mediaUrl || null,
      mediaThumbnailUrl: this.mediaThumbnailUrl || null,
      isVideo: this.isVideo,
    };

    this.dialogRef.close(reviewData);
  }

  skip() {
    this.dialogRef.close(null);
  }

  close() {
    const hasChanges =
      this.rating > 0 ||
      (this.reviewText && this.reviewText.trim().length > 0) ||
      !!this.mediaUrl;

    // Build confirmation copy depending on context
    let message: string;
    if (this.fromIssue) {
      message = hasChanges
        ? "If you close now, your reported issues and this review will not be submitted."
        : "If you close now, your reported issues will not be submitted.";
    } else if (hasChanges) {
      message = "Discard your current review and close without saving?";
    } else {
      // No changes and not from issue: just close quietly
      this.dialogRef.close(null);
      return;
    }

    // Use shared confirmation dialog model instead of browser confirm
    const confirmDialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: "480px",
      maxWidth: "95vw",
      data: {
        action: "DiscardReview",
        request: {
          contentTitle: this.flowData?.title || "this review",
          customMessage: message,
        },
        isIncoming: false,
      },
      panelClass: "dialoguebox",
    });

    confirmDialogRef.afterClosed().subscribe((result) => {
      if (result?.confirmed) {
        this.dialogRef.close(null);
      }
    });
  }
}


